import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RepeatIcon from '@mui/icons-material/Repeat';
import FlagIcon from '@mui/icons-material/Flag';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { ToastContainer, toast } from 'react-toastify';
import { ConfirmacionEliminar } from '../elementos/eventos/avisos/VentanaEmergente';
import { fetchFestivosCombinados, fetchFestivosByCalendario, fetchFestivosByCalendarioColab, crearFestivo, actualizarFestivo, eliminarFestivo } from '../../peticiones_API/PeticionesFestivos';
import { getDiasEspecialesByCalendario, getDiasEspecialesByCalendarioColab, createDiaEspecial, updateDiaEspecial, deleteDiaEspecial } from '../../peticiones_API/PeticionesDiasEspeciales';
import { fetchPeriodosVerano, crearPeriodoVerano, actualizarPeriodoVerano, eliminarPeriodoVerano } from '../../peticiones_API/PeticionesPeriodosVerano';
import { fetchPeriodosVeranoColab, crearPeriodoVeranoColab, actualizarPeriodoVeranoColab, eliminarPeriodoVeranoColab } from '../../peticiones_API/PeticionesPeriodosVeranoColab';
import { fetchCalendariosLaborales, crearCalendarioLaboral, actualizarCalendarioLaboral, eliminarCalendarioLaboral, duplicarCalendarioLaboral, fetchVistaAnualCalendario } from '../../peticiones_API/PeticionesCalendariosLaborales';
import { fetchCalendariosLaboralesColab, crearCalendarioLaboralColab, actualizarCalendarioLaboralColab, eliminarCalendarioLaboralColab, duplicarCalendarioLaboralColab, fetchVistaAnualCalendarioColab } from '../../peticiones_API/PeticionesCalendariosLaboralesColab';
import './GestorCalendariosLaborales.css';

const PROVINCIAS = [
  'A Coruna', 'Alava', 'Albacete', 'Alicante', 'Almeria', 'Asturias', 'Avila',
  'Badajoz', 'Barcelona', 'Burgos', 'Caceres', 'Cadiz', 'Cantabria', 'Castellon',
  'Ciudad Real', 'Cordoba', 'Cuenca', 'Girona', 'Granada', 'Guadalajara', 'Gipuzkoa',
  'Huelva', 'Huesca', 'Islas Baleares', 'Jaen', 'La Rioja', 'Las Palmas', 'Leon',
  'Lleida', 'Lugo', 'Madrid', 'Malaga', 'Murcia', 'Navarra', 'Ourense', 'Palencia',
  'Pontevedra', 'Salamanca', 'Santa Cruz de Tenerife', 'Segovia', 'Sevilla', 'Soria',
  'Tarragona', 'Teruel', 'Toledo', 'Valencia', 'Valladolid', 'Vizcaya', 'Zamora', 'Zaragoza'
];

const TIPO_CONFIG = {
  nacional: { label: 'Nacional', icon: FlagIcon, color: '#e91e63', bgColor: '#fce4ec' },
  empresa: { label: 'Empresa', icon: BusinessIcon, color: '#9c27b0', bgColor: '#f3e5f5' },
  provincial: { label: 'Provincial', icon: LocationOnIcon, color: '#ff9800', bgColor: '#fff3e0' },
  municipal: { label: 'Municipal', icon: LocationCityIcon, color: '#00bcd4', bgColor: '#e0f7fa' }
};

const COLORES_DIA = {
  laboral: { bg: '#e3f2fd', text: '#1565c0' },
  verano: { bg: '#fff8e1', text: '#f57c00' },
  festivo: { bg: '#ffebee', text: '#c62828' },
  finde: { bg: '#f5f5f5', text: '#9e9e9e' },
  especial: { bg: '#e8f5e9', text: '#2e7d32' }
};

function GestorCalendariosLaborales({ filtroNombreCalendario, excluirNombreCalendario, filtroNombrePeriodo, excluirNombrePeriodo, esColab = false } = {}) {

  const api = useMemo(() => esColab ? {
    fetchCalendarios: fetchCalendariosLaboralesColab,
    crearCalendario: crearCalendarioLaboralColab,
    actualizarCalendario: actualizarCalendarioLaboralColab,
    eliminarCalendario: eliminarCalendarioLaboralColab,
    duplicarCalendario: duplicarCalendarioLaboralColab,
    fetchVistaAnual: fetchVistaAnualCalendarioColab,
    fetchFestivosByCalendario: fetchFestivosByCalendarioColab,
    getDiasEspecialesByCalendario: getDiasEspecialesByCalendarioColab,
    calendarioFkField: 'calendario_laboral_colab_id',
    fetchPeriodos: (params) => fetchPeriodosVeranoColab(params),
    crearPeriodo: crearPeriodoVeranoColab,
    actualizarPeriodo: actualizarPeriodoVeranoColab,
    eliminarPeriodo: eliminarPeriodoVeranoColab,
    fetchFestivosCombinados: (anio) => fetchFestivosCombinados(anio, null, null, true),
    esColaborador: true,
  } : {
    fetchCalendarios: fetchCalendariosLaborales,
    crearCalendario: crearCalendarioLaboral,
    actualizarCalendario: actualizarCalendarioLaboral,
    eliminarCalendario: eliminarCalendarioLaboral,
    duplicarCalendario: duplicarCalendarioLaboral,
    fetchVistaAnual: fetchVistaAnualCalendario,
    fetchFestivosByCalendario: fetchFestivosByCalendario,
    getDiasEspecialesByCalendario: getDiasEspecialesByCalendario,
    calendarioFkField: 'calendario_laboral_id',
    fetchPeriodos: fetchPeriodosVerano,
    crearPeriodo: crearPeriodoVerano,
    actualizarPeriodo: actualizarPeriodoVerano,
    eliminarPeriodo: eliminarPeriodoVerano,
    fetchFestivosCombinados: fetchFestivosCombinados,
    esColaborador: false,
  }, [esColab]);

  const [loading, setLoading] = useState(true);
  const [filtroAnio, setFiltroAnio] = useState(new Date().getFullYear());

  const [festivos, setFestivos] = useState({ nacional: [], empresa: [], provincial: [], municipal: [] });
  const [showFestivoModal, setShowFestivoModal] = useState(false);
  const [editandoFestivo, setEditandoFestivo] = useState(null);
  const [festivoForm, setFestivoForm] = useState({
    fecha: '', nombre: '', tipo: 'nacional', anio: new Date().getFullYear(), provincia: '', municipio: '', permanente: false
  });

  const [periodos, setPeriodos] = useState([]);
  const [showPeriodoModal, setShowPeriodoModal] = useState(false);
  const [editandoPeriodo, setEditandoPeriodo] = useState(null);
  const [periodoForm, setPeriodoForm] = useState({
    nombre: '', anio: new Date().getFullYear(), fecha_inicio: '', fecha_fin: '', dias_obligatorios: 10
  });

  const [calendarios, setCalendarios] = useState([]);
  const [showCalendarioModal, setShowCalendarioModal] = useState(false);
  const [editandoCalendario, setEditandoCalendario] = useState(null);
  const [calendarioForm, setCalendarioForm] = useState({
    nombre: '', anio: new Date().getFullYear(), provincia: '', municipio: '',
    horas_lunes_jueves: 8.5, horas_viernes: 7, horas_verano: 7, periodo_verano_id: '',
    dias_vacaciones_verano: 0, dias_vacaciones_normal: 0, horas_convenio: ''
  });
  const [showDuplicarModal, setShowDuplicarModal] = useState(false);
  const [calendarioDuplicar, setCalendarioDuplicar] = useState(null);
  const [nuevoAnioDuplicar, setNuevoAnioDuplicar] = useState(new Date().getFullYear() + 1);

  const [showVistaAnual, setShowVistaAnual] = useState(false);
  const [vistaAnualData, setVistaAnualData] = useState(null);
  const [loadingVistaAnual, setLoadingVistaAnual] = useState(false);
  const [calendarioVistaActual, setCalendarioVistaActual] = useState(null);
  const [festivosCalendario, setFestivosCalendario] = useState({ provincial: [], municipal: [] });
  const [diasEspecialesCalendario, setDiasEspecialesCalendario] = useState([]);

  const [showDiaEspecialModal, setShowDiaEspecialModal] = useState(false);
  const [editandoDiaEspecial, setEditandoDiaEspecial] = useState(null);
  const [diaEspecialForm, setDiaEspecialForm] = useState({
    fecha: '', nombre: '', horas_trabajo: 5, anio: new Date().getFullYear(), permanente: false
  });
  const [totalesCalendarios, setTotalesCalendarios] = useState({});
  const [festivosEditModal, setFestivosEditModal] = useState({ provincial: [], municipal: [] });
  const [diasEspecialesEditModal, setDiasEspecialesEditModal] = useState([]);
  const [loadingEditModalData, setLoadingEditModalData] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const confirmDialog = ConfirmacionEliminar();
  const anios = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i);

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const [festivosData, periodosData, calendariosData] = await Promise.all([
        api.fetchFestivosCombinados(filtroAnio), api.fetchPeriodos({ anio: filtroAnio }), api.fetchCalendarios(filtroAnio)
      ]);
      setFestivos(festivosData || { nacional: [], empresa: [], provincial: [], municipal: [] });
      let pers = periodosData || [];
      if (filtroNombrePeriodo) {
        const filtro = filtroNombrePeriodo.toLowerCase();
        pers = pers.filter(p => p.nombre?.toLowerCase().includes(filtro));
      }
      if (excluirNombrePeriodo) {
        const excluir = excluirNombrePeriodo.toLowerCase();
        pers = pers.filter(p => !p.nombre?.toLowerCase().includes(excluir));
      }
      setPeriodos(pers);
      let cals = calendariosData || [];
      if (filtroNombreCalendario) {
        const filtro = filtroNombreCalendario.toLowerCase();
        cals = cals.filter(c => c.nombre?.toLowerCase().includes(filtro));
      }
      if (excluirNombreCalendario) {
        const excluir = excluirNombreCalendario.toLowerCase();
        cals = cals.filter(c => !c.nombre?.toLowerCase().includes(excluir));
      }
      setCalendarios(cals);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, [filtroAnio, filtroNombreCalendario, excluirNombreCalendario, filtroNombrePeriodo, excluirNombrePeriodo, api]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  useEffect(() => { setPage(0); }, [filtroAnio]);

  useEffect(() => {
    const cargarTotales = async () => {
      if (calendarios.length === 0) return;

      const totales = {};
      for (const cal of calendarios) {
        try {
          const data = await api.fetchVistaAnual(cal.Id);
          const horasVerano = parseFloat(cal.horas_verano) || 7;
          const horasLJ = parseFloat(cal.horas_lunes_jueves) || 8.5;
          const horasV = parseFloat(cal.horas_viernes) || 7;
          const horasNormalDia = (horasLJ * 4 + horasV) / 5;
          const vacVerano = (cal.dias_vacaciones_verano || 0) * horasVerano;
          const vacNormal = (cal.dias_vacaciones_normal || 0) * horasNormalDia;
          const horasConvenio = cal.horas_convenio ? parseFloat(cal.horas_convenio) : 0;
          const horasBrutas = data.totalAnual;
          const horasNetas = horasBrutas - vacVerano - vacNormal;
          const horasTrabajo = horasConvenio ? horasNetas - horasConvenio : horasNetas;

          totales[cal.Id] = {
            horasBrutas,
            vacVerano,
            vacNormal,
            horasConvenio,
            horasNetas,
            horasTrabajo
          };
        } catch (error) {
          console.error(`Error al cargar totales para calendario ${cal.Id}:`, error);
        }
      }
      setTotalesCalendarios(totales);
    };

    cargarTotales();
  }, [calendarios]);

  const openFestivoModal = (tipo) => {
    setEditandoFestivo(null);
    setFestivoForm({ fecha: '', nombre: '', tipo, anio: filtroAnio, provincia: '', municipio: '', permanente: false });
    setShowFestivoModal(true);
  };

  const handleEditarFestivo = (festivo) => {
    setEditandoFestivo(festivo);
    setFestivoForm({
      fecha: festivo.fecha?.split('T')[0] || '', nombre: festivo.nombre, tipo: festivo.tipo,
      anio: festivo.anio, provincia: festivo.provincia || '', municipio: festivo.municipio || '',
      permanente: festivo.permanente || false,
      [api.calendarioFkField]: festivo[api.calendarioFkField] || null
    });
    setShowFestivoModal(true);
  };

  const handleGuardarFestivo = async (e) => {
    e.preventDefault();
    try {
      const formData = { ...festivoForm };
      if (formData.tipo === 'nacional' || formData.tipo === 'empresa') {
        formData.es_colaborador = api.esColaborador;
      }
      if (editandoFestivo) {
        await actualizarFestivo(editandoFestivo.Id || editandoFestivo.id, formData);
        toast.success('Festivo actualizado');
      } else {
        await crearFestivo(formData);
        toast.success('Festivo creado');
      }
      setShowFestivoModal(false);
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEliminarFestivo = async (id) => {
    const confirmado = await confirmDialog.showAlert('eliminar', 'festivo');
    if (confirmado) {
      try {
        await eliminarFestivo(id);
        toast.success('Festivo eliminado');
        cargarDatos();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar');
      }
    }
  };

  const openPeriodoModal = () => {
    setEditandoPeriodo(null);
    setPeriodoForm({ nombre: '', anio: filtroAnio, fecha_inicio: '', fecha_fin: '', dias_obligatorios: 10 });
    setShowPeriodoModal(true);
  };

  const handleEditarPeriodo = (periodo) => {
    setEditandoPeriodo(periodo);
    setPeriodoForm({
      nombre: periodo.nombre || '', anio: periodo.anio, fecha_inicio: periodo.fecha_inicio?.split('T')[0] || '',
      fecha_fin: periodo.fecha_fin?.split('T')[0] || '', dias_obligatorios: periodo.dias_obligatorios
    });
    setShowPeriodoModal(true);
  };

  const handleGuardarPeriodo = async (e) => {
    e.preventDefault();
    try {
      if (editandoPeriodo) {
        await api.actualizarPeriodo(editandoPeriodo.Id, periodoForm);
        toast.success('Periodo actualizado');
      } else {
        await api.crearPeriodo(periodoForm);
        toast.success('Periodo creado');
      }
      setShowPeriodoModal(false);
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEliminarPeriodo = async (id) => {
    const confirmado = await confirmDialog.showAlert('eliminar', 'periodo de verano');
    if (confirmado) {
      try {
        await api.eliminarPeriodo(id);
        toast.success('Periodo eliminado');
        cargarDatos();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar');
      }
    }
  };

  const openCalendarioModal = () => {
    setEditandoCalendario(null);
    const periodoDelAnio = periodos.find(p => p.anio === filtroAnio);
    setCalendarioForm({
      nombre: '', anio: filtroAnio, provincia: '', municipio: '',
      horas_lunes_jueves: 8.5, horas_viernes: 7, horas_verano: 7,
      periodo_verano_id: periodoDelAnio?.Id || '', dias_vacaciones_verano: 0, dias_vacaciones_normal: 0,
      horas_convenio: ''
    });
    setFestivosEditModal({ provincial: [], municipal: [] });
    setDiasEspecialesEditModal([]);
    setShowCalendarioModal(true);
  };

  const recargarFestivosEditModal = async () => {
    if (!editandoCalendario) return;
    try {
      const [festivosData, diasEspData] = await Promise.all([
        api.fetchFestivosByCalendario(editandoCalendario.Id, editandoCalendario.anio),
        api.getDiasEspecialesByCalendario(editandoCalendario.Id, editandoCalendario.anio)
      ]);
      setFestivosEditModal({
        provincial: festivosData.provincial || [],
        municipal: festivosData.municipal || []
      });
      setDiasEspecialesEditModal(diasEspData || []);
    } catch (error) {
      console.error('Error al recargar datos:', error);
    }
  };

  const openFestivoEditModal = (tipo) => {
    if (!editandoCalendario) return;
    setEditandoFestivo(null);
    setFestivoForm({
      fecha: '', nombre: '', tipo, anio: editandoCalendario.anio,
      provincia: editandoCalendario.provincia || '',
      municipio: editandoCalendario.municipio || '',
      permanente: false,
      [api.calendarioFkField]: editandoCalendario.Id
    });
    setShowFestivoModal(true);
  };

  const handleGuardarFestivoEditModal = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...festivoForm,
        [api.calendarioFkField]: editandoCalendario?.Id || festivoForm[api.calendarioFkField]
      };
      if (editandoFestivo) {
        await actualizarFestivo(editandoFestivo.Id || editandoFestivo.id, dataToSend);
        toast.success('Festivo actualizado');
      } else {
        await crearFestivo(dataToSend);
        toast.success('Festivo creado');
      }
      setShowFestivoModal(false);
      await recargarFestivosEditModal();
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEliminarFestivoEditModal = async (id) => {
    const confirmado = await confirmDialog.showAlert('eliminar', 'festivo');
    if (confirmado) {
      try {
        await eliminarFestivo(id);
        toast.success('Festivo eliminado');
        await recargarFestivosEditModal();
        cargarDatos();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar');
      }
    }
  };

  const openDiaEspecialEditModal = () => {
    if (!editandoCalendario) return;
    setEditandoDiaEspecial(null);
    setDiaEspecialForm({
      fecha: '', nombre: '', horas_trabajo: 5, anio: editandoCalendario.anio, permanente: false
    });
    setShowDiaEspecialModal(true);
  };

  const handleGuardarDiaEspecialEditModal = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...diaEspecialForm,
        [api.calendarioFkField]: editandoCalendario.Id
      };
      if (editandoDiaEspecial) {
        await updateDiaEspecial(editandoDiaEspecial.Id || editandoDiaEspecial.id, dataToSend);
        toast.success('Día especial actualizado');
      } else {
        await createDiaEspecial(dataToSend);
        toast.success('Día especial creado');
      }
      setShowDiaEspecialModal(false);
      await recargarFestivosEditModal();
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEliminarDiaEspecialEditModal = async (id) => {
    const confirmado = await confirmDialog.showAlert('eliminar', 'día especial');
    if (confirmado) {
      try {
        await deleteDiaEspecial(id);
        toast.success('Día especial eliminado');
        await recargarFestivosEditModal();
        cargarDatos();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar');
      }
    }
  };

  const handleEditarCalendario = async (calendario) => {
    setEditandoCalendario(calendario);
    setCalendarioForm({
      nombre: calendario.nombre, anio: calendario.anio, provincia: calendario.provincia,
      municipio: calendario.municipio || '',
      horas_lunes_jueves: parseFloat(calendario.horas_lunes_jueves) || 8.5,
      horas_viernes: parseFloat(calendario.horas_viernes) || 7,
      horas_verano: parseFloat(calendario.horas_verano) || 7,
      periodo_verano_id: calendario.periodo_verano_id || '',
      dias_vacaciones_verano: calendario.dias_vacaciones_verano || 0,
      dias_vacaciones_normal: calendario.dias_vacaciones_normal || 0,
      horas_convenio: calendario.horas_convenio || ''
    });
    setShowCalendarioModal(true);

    setLoadingEditModalData(true);
    try {
      const [festivosData, diasEspData] = await Promise.all([
        api.fetchFestivosByCalendario(calendario.Id, calendario.anio),
        api.getDiasEspecialesByCalendario(calendario.Id, calendario.anio)
      ]);
      setFestivosEditModal({
        provincial: festivosData.provincial || [],
        municipal: festivosData.municipal || []
      });
      setDiasEspecialesEditModal(diasEspData || []);
    } catch (error) {
      console.error('Error al cargar festivos/días especiales:', error);
    } finally {
      setLoadingEditModalData(false);
    }
  };

  const handleGuardarCalendario = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...calendarioForm,
        periodo_verano_id: calendarioForm.periodo_verano_id || null,
        horas_convenio: calendarioForm.horas_convenio ? parseFloat(calendarioForm.horas_convenio) : null
      };
      if (editandoCalendario) {
        await api.actualizarCalendario(editandoCalendario.Id, dataToSend);
        toast.success('Calendario actualizado');
      } else {
        await api.crearCalendario(dataToSend);
        toast.success('Calendario creado');
      }
      setShowCalendarioModal(false);
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEliminarCalendario = async (id) => {
    const confirmado = await confirmDialog.showAlert('eliminar', 'calendario laboral');
    if (confirmado) {
      try {
        await api.eliminarCalendario(id);
        toast.success('Calendario eliminado');
        cargarDatos();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar');
      }
    }
  };

  const openDuplicarModal = (calendario) => {
    setCalendarioDuplicar(calendario);
    setNuevoAnioDuplicar(calendario.anio + 1);
    setShowDuplicarModal(true);
  };

  const handleDuplicarCalendario = async () => {
    try {
      await api.duplicarCalendario(calendarioDuplicar.Id, nuevoAnioDuplicar);
      toast.success('Calendario duplicado correctamente');
      setShowDuplicarModal(false);
      setFiltroAnio(nuevoAnioDuplicar);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al duplicar');
    }
  };

  const handleVerVistaAnual = async (calendario) => {
    try {
      setLoadingVistaAnual(true);
      setShowVistaAnual(true);
      setCalendarioVistaActual(calendario);

      const [data, festivosData, diasEspData] = await Promise.all([
        api.fetchVistaAnual(calendario.Id),
        api.fetchFestivosByCalendario(calendario.Id, calendario.anio),
        api.getDiasEspecialesByCalendario(calendario.Id, calendario.anio)
      ]);

      setVistaAnualData(data);
      setFestivosCalendario({
        provincial: festivosData.provincial || [],
        municipal: festivosData.municipal || []
      });
      setDiasEspecialesCalendario(diasEspData || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar la vista anual');
      setShowVistaAnual(false);
    } finally {
      setLoadingVistaAnual(false);
    }
  };

  const recargarVistaAnual = async () => {
    if (calendarioVistaActual) {
      const [data, festivosData, diasEspData] = await Promise.all([
        api.fetchVistaAnual(calendarioVistaActual.Id),
        api.fetchFestivosByCalendario(calendarioVistaActual.Id, calendarioVistaActual.anio),
        api.getDiasEspecialesByCalendario(calendarioVistaActual.Id, calendarioVistaActual.anio)
      ]);
      setVistaAnualData(data);
      setFestivosCalendario({
        provincial: festivosData.provincial || [],
        municipal: festivosData.municipal || []
      });
      setDiasEspecialesCalendario(diasEspData || []);
    }
  };

  const openFestivoCalendarioModal = (tipo) => {
    if (!calendarioVistaActual) return;
    setEditandoFestivo(null);
    setFestivoForm({
      fecha: '', nombre: '', tipo, anio: calendarioVistaActual.anio,
      provincia: calendarioVistaActual.provincia || '',
      municipio: calendarioVistaActual.municipio || '',
      permanente: false,
      [api.calendarioFkField]: calendarioVistaActual.Id
    });
    setShowFestivoModal(true);
  };

  const handleGuardarFestivoCalendario = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...festivoForm,
        [api.calendarioFkField]: calendarioVistaActual?.Id || festivoForm[api.calendarioFkField]
      };
      if (editandoFestivo) {
        await actualizarFestivo(editandoFestivo.Id || editandoFestivo.id, dataToSend);
        toast.success('Festivo actualizado');
      } else {
        await crearFestivo(dataToSend);
        toast.success('Festivo creado');
      }
      setShowFestivoModal(false);
      if (calendarioVistaActual) {
        await recargarVistaAnual();
      }
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEliminarFestivoCalendario = async (id) => {
    const confirmado = await confirmDialog.showAlert('eliminar', 'festivo');
    if (confirmado) {
      try {
        await eliminarFestivo(id);
        toast.success('Festivo eliminado');
        await recargarVistaAnual();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar');
      }
    }
  };

  const openDiaEspecialModal = () => {
    if (!calendarioVistaActual) return;
    setEditandoDiaEspecial(null);
    setDiaEspecialForm({
      fecha: '', nombre: '', horas_trabajo: 5, anio: calendarioVistaActual.anio, permanente: false
    });
    setShowDiaEspecialModal(true);
  };

  const handleEditarDiaEspecial = (dia) => {
    setEditandoDiaEspecial(dia);
    setDiaEspecialForm({
      fecha: dia.fecha?.split('T')[0] || '', nombre: dia.nombre,
      horas_trabajo: parseFloat(dia.horas_trabajo) || 5, anio: dia.anio,
      permanente: dia.permanente || false
    });
    setShowDiaEspecialModal(true);
  };

  const handleGuardarDiaEspecial = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...diaEspecialForm,
        [api.calendarioFkField]: calendarioVistaActual.Id
      };
      if (editandoDiaEspecial) {
        await updateDiaEspecial(editandoDiaEspecial.Id || editandoDiaEspecial.id, dataToSend);
        toast.success('Día especial actualizado');
      } else {
        await createDiaEspecial(dataToSend);
        toast.success('Día especial creado');
      }
      setShowDiaEspecialModal(false);
      await recargarVistaAnual();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEliminarDiaEspecial = async (id) => {
    const confirmado = await confirmDialog.showAlert('eliminar', 'día especial');
    if (confirmado) {
      try {
        await deleteDiaEspecial(id);
        toast.success('Día especial eliminado');
        await recargarVistaAnual();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar');
      }
    }
  };

  const calcularHorasVacaciones = (dias, horasPorDia) => (dias * horasPorDia).toFixed(1);

  const renderFestivosList = (lista, tipo) => {
    const config = TIPO_CONFIG[tipo];
    const Icon = config.icon;
    if (!lista || lista.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Icon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
          <Typography color="text.secondary">No hay festivos {config.label.toLowerCase()}es</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {lista.map((festivo) => (
          <Box key={festivo.Id || festivo.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1, backgroundColor: config.bgColor, border: `1px solid ${config.color}30` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Icon sx={{ color: config.color }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{festivo.nombre}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(festivo.fecha).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                  {festivo.provincia && ` - ${festivo.provincia}`}
                  {festivo.municipio && ` (${festivo.municipio})`}
                  {festivo.permanente && <Tooltip title="Se repite todos los años"><RepeatIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: '#4caf50' }} /></Tooltip>}
                </Typography>
              </Box>
            </Box>
            <Box>
              <IconButton size="small" onClick={() => handleEditarFestivo(festivo)}><EditIcon fontSize="small" /></IconButton>
              <IconButton size="small" color="error" onClick={() => handleEliminarFestivo(festivo.Id || festivo.id)}><DeleteIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderMesCalendario = (mesData) => {
    const diasSemana = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
    const primerDia = mesData.dias[0]?.diaSemana;
    const offsetInicio = primerDia === 0 ? 6 : primerDia - 1;
    const diasConOffset = [...Array(offsetInicio).fill(null), ...mesData.dias];
    const semanas = [];
    for (let i = 0; i < diasConOffset.length; i += 7) {
      semanas.push(diasConOffset.slice(i, i + 7));
    }
    return (
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1, backgroundColor: 'white' }}>
        <Typography variant="subtitle2" align="center" sx={{ fontWeight: 'bold', mb: 1 }}>{mesData.nombre}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', mb: 1 }}>
          {diasSemana.map((d, i) => (
            <Typography key={i} variant="caption" align="center" sx={{ fontWeight: 'bold', color: i >= 5 ? '#9e9e9e' : '#333' }}>{d}</Typography>
          ))}
        </Box>
        {semanas.map((semana, sIdx) => (
          <Box key={sIdx} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
            {semana.map((dia, dIdx) => (
              <Box key={dIdx} sx={{
                width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '4px', fontSize: '12px', fontWeight: dia?.tipo === 'festivo' ? 'bold' : 'normal',
                backgroundColor: dia ? COLORES_DIA[dia.tipo]?.bg : 'transparent',
                color: dia ? COLORES_DIA[dia.tipo]?.text : 'transparent'
              }}>{dia?.dia || ''}</Box>
            ))}
          </Box>
        ))}
        <Typography variant="caption" align="center" sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>
          Total: <span style={{ color: '#1565c0' }}>{mesData.totalHoras}h</span>
        </Typography>
      </Box>
    );
  };

  if (loading) {
    return (<div className="gestor-calendarios-loading"><div className="spinner"></div><p>Cargando calendarios...</p></div>);
  }

  return (
    <div className="gestor-calendarios-laborales">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#336385' }}>Calendarios Laborales</Typography>
          <Typography variant="body2" color="text.secondary">Gestiona calendarios laborales, festivos y periodos de verano</Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Año</InputLabel>
          <Select value={filtroAnio} label="Año" onChange={(e) => setFiltroAnio(e.target.value)}>
            {anios.map((a) => (<MenuItem key={a} value={a}>{a}</MenuItem>))}
          </Select>
        </FormControl>
      </Box>

      <Card sx={{ mb: 3, backgroundColor: '#e8f5e9', border: '1px solid #81c784' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarMonthIcon sx={{ color: '#388e3c' }} />
              <Typography variant="h6">Calendarios Laborales</Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCalendarioModal} sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}>Nuevo Calendario</Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Define los horarios y días de vacaciones para cada ubicacion</Typography>
          <Divider sx={{ mb: 2 }} />
          {calendarios.length > 0 ? (
            <>
            <TableContainer component={Paper} sx={{ backgroundColor: 'white' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Nombre</strong></TableCell>
                    <TableCell><strong>Provincia</strong></TableCell>
                    <TableCell align="center"><strong>Horas L-J</strong></TableCell>
                    <TableCell align="center"><strong>Horas V</strong></TableCell>
                    <TableCell align="center"><strong>Horas Verano</strong></TableCell>
                    <TableCell align="center"><strong>Vac. Verano</strong></TableCell>
                    <TableCell align="center"><strong>Vac. Normal</strong></TableCell>
                    <TableCell align="center"><strong>Horas Convenio</strong></TableCell>
                    <TableCell align="center"><strong>Total Horas</strong></TableCell>
                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...calendarios]
                    .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((cal) => {
                    const totales = totalesCalendarios[cal.Id];
                    return (
                      <React.Fragment key={cal.Id}>
                        <TableRow hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{cal.nombre}</Typography>
                            {cal.municipio && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{cal.municipio}</Typography>}
                          </TableCell>
                          <TableCell>{cal.provincia}</TableCell>
                          <TableCell align="center">{parseFloat(cal.horas_lunes_jueves)}h</TableCell>
                          <TableCell align="center">{parseFloat(cal.horas_viernes)}h</TableCell>
                          <TableCell align="center">{parseFloat(cal.horas_verano)}h</TableCell>
                          <TableCell align="center"><Chip label={`${cal.dias_vacaciones_verano} días`} size="small" sx={{ backgroundColor: '#fff3e0', color: '#e65100' }} /></TableCell>
                          <TableCell align="center"><Chip label={`${cal.dias_vacaciones_normal} días`} size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1565c0' }} /></TableCell>
                          <TableCell align="center">{cal.horas_convenio ? `${parseFloat(cal.horas_convenio)}h` : '-'}</TableCell>
                          <TableCell align="center">
                            {totales ? (
                              <Tooltip title={
                                <Box sx={{ p: 1 }}>
                                  <Typography variant="caption" sx={{ display: 'block' }}>Horas brutas: {totales.horasBrutas?.toFixed(1)}h</Typography>
                                  <Typography variant="caption" sx={{ display: 'block' }}>- Vac. verano: {totales.vacVerano?.toFixed(1)}h</Typography>
                                  <Typography variant="caption" sx={{ display: 'block' }}>- Vac. normal: {totales.vacNormal?.toFixed(1)}h</Typography>
                                  {totales.horasConvenio > 0 && <Typography variant="caption" sx={{ display: 'block' }}>- Convenio: {totales.horasConvenio?.toFixed(1)}h</Typography>}
                                  <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.3)' }} />
                                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', color: totales.horasTrabajo < 0 ? '#ff8a80' : 'inherit' }}>= Total: {totales.horasTrabajo?.toFixed(1)}h</Typography>
                                </Box>
                              }>
                                <Chip
                                  label={`${totales.horasTrabajo?.toFixed(1)}h`}
                                  size="small"
                                  sx={{
                                    backgroundColor: totales.horasTrabajo < 0 ? '#ffebee' : '#e8f5e9',
                                    color: totales.horasTrabajo < 0 ? '#c62828' : '#2e7d32',
                                    fontWeight: 'bold',
                                    cursor: 'help'
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">...</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Ver calendario anual"><IconButton size="small" onClick={() => handleVerVistaAnual(cal)} sx={{ color: '#388e3c' }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEditarCalendario(cal)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Duplicar para otro año"><IconButton size="small" onClick={() => openDuplicarModal(cal)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => handleEliminarCalendario(cal.Id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                        {totales && (
                          <TableRow sx={{ backgroundColor: '#fafafa' }}>
                            <TableCell colSpan={10} sx={{ py: 1, borderBottom: '2px solid #e0e0e0' }}>
                              <Typography variant="caption" sx={{ color: '#616161' }}>
                                <strong>Desglose:</strong>{' '}
                                Horas brutas: <strong>{totales.horasBrutas?.toFixed(1)}h</strong>
                                {' '}- Vac. verano: <strong>{totales.vacVerano?.toFixed(1)}h</strong>
                                {' '}- Vac. normal: <strong>{totales.vacNormal?.toFixed(1)}h</strong>
                                {totales.horasConvenio > 0 && <>{' '}- Convenio: <strong>{totales.horasConvenio?.toFixed(1)}h</strong></>}
                                {' '}= <strong style={{ color: totales.horasTrabajo < 0 ? '#c62828' : '#2e7d32' }}>{totales.horasTrabajo?.toFixed(1)}h</strong>
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#336385', fontWeight: 'medium' }}>REGISTROS A MOSTRAR</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  sx={{ minWidth: 70 }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={75}>75</MenuItem>
                </Select>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  sx={{ color: '#336385' }}
                >
                  {'<'}
                </IconButton>
                {(() => {
                  const totalPages = Math.ceil(calendarios.length / rowsPerPage);
                  const pages = [];
                  if (totalPages <= 5) {
                    for (let i = 0; i < totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(0);
                    if (page > 2) pages.push('...');
                    for (let i = Math.max(1, page - 1); i <= Math.min(totalPages - 2, page + 1); i++) {
                      if (!pages.includes(i)) pages.push(i);
                    }
                    if (page < totalPages - 3) pages.push('...');
                    pages.push(totalPages - 1);
                  }
                  return pages.map((p, idx) => (
                    p === '...' ? (
                      <Typography key={`ellipsis-${idx}`} variant="body2" sx={{ px: 1 }}>...</Typography>
                    ) : (
                      <Button
                        key={p}
                        size="small"
                        variant={page === p ? 'contained' : 'text'}
                        onClick={() => setPage(p)}
                        sx={{
                          minWidth: 32,
                          height: 32,
                          p: 0,
                          backgroundColor: page === p ? '#336385' : 'transparent',
                          color: page === p ? 'white' : '#336385',
                          '&:hover': { backgroundColor: page === p ? '#27496d' : '#e3f2fd' }
                        }}
                      >
                        {p + 1}
                      </Button>
                    )
                  ));
                })()}
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.min(Math.ceil(calendarios.length / rowsPerPage) - 1, page + 1))}
                  disabled={page >= Math.ceil(calendarios.length / rowsPerPage) - 1}
                  sx={{ color: '#336385' }}
                >
                  {'>'}
                </IconButton>
              </Box>
            </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CalendarMonthIcon sx={{ fontSize: 48, color: '#a5d6a7', mb: 1 }} />
              <Typography color="text.secondary">No hay calendarios laborales para {filtroAnio}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, backgroundColor: '#fff8e1', border: '1px solid #ffcc80' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WbSunnyIcon sx={{ color: '#ff9800' }} />
              <Typography variant="h6">Periodos de Verano</Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openPeriodoModal} sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}>Nuevo Periodo</Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Define los periodos de verano con jornada reducida</Typography>
          <Divider sx={{ mb: 2 }} />
          {periodos.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {periodos.map((periodo) => (
                <Box key={periodo.Id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: periodo.anio === filtroAnio ? 'white' : '#fafafa', borderRadius: 1, border: periodo.anio === filtroAnio ? '2px solid #ff9800' : '1px solid #ffcc80' }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{periodo.nombre || `Periodo Verano ${periodo.anio}`}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(periodo.fecha_inicio).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })} - {new Date(periodo.fecha_fin).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                    </Typography>
                    <Chip label={`${periodo.dias_obligatorios} días obligatorios`} size="small" sx={{ mt: 0.5, backgroundColor: '#ff9800', color: 'white' }} />
                  </Box>
                  <Box>
                    <IconButton onClick={() => handleEditarPeriodo(periodo)}><EditIcon /></IconButton>
                    <IconButton color="error" onClick={() => handleEliminarPeriodo(periodo.Id)}><DeleteIcon /></IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <WbSunnyIcon sx={{ fontSize: 48, color: '#ffcc80', mb: 1 }} />
              <Typography color="text.secondary">No hay periodos de verano configurados</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><FlagIcon sx={{ color: '#e91e63' }} /><Typography variant="h6">Festivos Nacionales</Typography></Box>
                <IconButton size="small" onClick={() => openFestivoModal('nacional')} sx={{ backgroundColor: '#336385', color: 'white', '&:hover': { backgroundColor: '#27496d' } }}><AddIcon fontSize="small" /></IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Se aplican a todos los calendarios laborales</Typography>
              <Divider sx={{ mb: 2 }} />
              {renderFestivosList(festivos.nacional, 'nacional')}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BusinessIcon sx={{ color: '#9c27b0' }} /><Typography variant="h6">Festivos Empresa</Typography></Box>
                <IconButton size="small" onClick={() => openFestivoModal('empresa')} sx={{ backgroundColor: '#336385', color: 'white', '&:hover': { backgroundColor: '#27496d' } }}><AddIcon fontSize="small" /></IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Se aplican a todos los calendarios laborales</Typography>
              <Divider sx={{ mb: 2 }} />
              {renderFestivosList(festivos.empresa, 'empresa')}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80', mb: 3 }}>
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOnIcon sx={{ color: '#ff9800', fontSize: 20 }} />
          <LocationCityIcon sx={{ color: '#00bcd4', fontSize: 20 }} />
          <StarIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
          <strong>Festivos Provinciales, Municipales y Días Especiales</strong>: Se gestionan dentro de cada calendario laboral (boton de editar o ver vista anual)
        </Typography>
      </Box>

      <Dialog open={showCalendarioModal} onClose={() => setShowCalendarioModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>
          {editandoCalendario ? 'Editar Calendario Laboral' : 'Nuevo Calendario Laboral'}
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>Configura los horarios y vacaciones para {calendarioForm.anio}</Typography>
        </DialogTitle>
        <form onSubmit={handleGuardarCalendario}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth margin="normal" label="Nombre" value={calendarioForm.nombre} onChange={(e) => setCalendarioForm({ ...calendarioForm, nombre: e.target.value })} placeholder="Ej: Valencia Capital 2026" required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Provincia *</InputLabel>
                  <Select value={calendarioForm.provincia} label="Provincia *" onChange={(e) => setCalendarioForm({ ...calendarioForm, provincia: e.target.value })} required>
                    {PROVINCIAS.map((prov) => (<MenuItem key={prov} value={prov}>{prov}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Municipio (opcional)" value={calendarioForm.municipio} onChange={(e) => setCalendarioForm({ ...calendarioForm, municipio: e.target.value })} placeholder="Ej: Valencia, Paterna, Torrent..." />
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#336385', display: 'flex', alignItems: 'center', gap: 1 }}><AccessTimeIcon fontSize="small" /> Horas de Trabajo</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}><TextField fullWidth label="Horas L-J" type="number" value={calendarioForm.horas_lunes_jueves} onChange={(e) => setCalendarioForm({ ...calendarioForm, horas_lunes_jueves: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0, max: 12, step: 0.25 }} required /></Grid>
              <Grid item xs={4}><TextField fullWidth label="Horas Viernes" type="number" value={calendarioForm.horas_viernes} onChange={(e) => setCalendarioForm({ ...calendarioForm, horas_viernes: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0, max: 12, step: 0.25 }} required /></Grid>
              <Grid item xs={4}><TextField fullWidth label="Horas Verano" type="number" value={calendarioForm.horas_verano} onChange={(e) => setCalendarioForm({ ...calendarioForm, horas_verano: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0, max: 12, step: 0.25 }} required /></Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#336385', display: 'flex', alignItems: 'center', gap: 1 }}><WbSunnyIcon fontSize="small" /> Periodo de Verano</Typography>
            <FormControl fullWidth>
              <InputLabel>Periodo de Verano</InputLabel>
              <Select value={calendarioForm.periodo_verano_id} label="Periodo de Verano" onChange={(e) => setCalendarioForm({ ...calendarioForm, periodo_verano_id: e.target.value })}>
                <MenuItem value="">Sin periodo de verano</MenuItem>
                {periodos.map((p) => (<MenuItem key={p.Id} value={p.Id}>{p.nombre || `Periodo ${p.anio}`}: {new Date(p.fecha_inicio).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - {new Date(p.fecha_fin).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</MenuItem>))}
              </Select>
            </FormControl>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#336385', display: 'flex', alignItems: 'center', gap: 1 }}><BeachAccessIcon fontSize="small" /> Días de Vacaciones</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}><TextField fullWidth label={`Días Vacaciones Verano (${calendarioForm.horas_verano}h/día)`} type="number" value={calendarioForm.dias_vacaciones_verano} onChange={(e) => setCalendarioForm({ ...calendarioForm, dias_vacaciones_verano: parseInt(e.target.value) || 0 })} inputProps={{ min: 0, max: 30 }} helperText={`Se restan ${calcularHorasVacaciones(calendarioForm.dias_vacaciones_verano, calendarioForm.horas_verano)}h del total`} /></Grid>
              <Grid item xs={6}><TextField fullWidth label={`Días Vacaciones Normal (${((calendarioForm.horas_lunes_jueves * 4 + calendarioForm.horas_viernes) / 5).toFixed(1)}h/día)`} type="number" value={calendarioForm.dias_vacaciones_normal} onChange={(e) => setCalendarioForm({ ...calendarioForm, dias_vacaciones_normal: parseInt(e.target.value) || 0 })} inputProps={{ min: 0, max: 30 }} helperText={`Se restan ${calcularHorasVacaciones(calendarioForm.dias_vacaciones_normal, (calendarioForm.horas_lunes_jueves * 4 + calendarioForm.horas_viernes) / 5)}h del total`} /></Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <TextField fullWidth label="Horas del Convenio (anual)" type="number" value={calendarioForm.horas_convenio} onChange={(e) => setCalendarioForm({ ...calendarioForm, horas_convenio: e.target.value })} inputProps={{ min: 0, max: 3000, step: 1 }} helperText="Horas anuales segun convenio colectivo (opcional)" />

            {editandoCalendario && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#336385', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagIcon fontSize="small" /> Festivos del Calendario
                </Typography>

                {loadingEditModalData ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary">Cargando...</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: TIPO_CONFIG.provincial.bgColor, borderRadius: 1, border: `1px solid ${TIPO_CONFIG.provincial.color}30`, height: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon sx={{ color: TIPO_CONFIG.provincial.color, fontSize: 18 }} /> Provinciales
                          </Typography>
                          <IconButton size="small" onClick={() => openFestivoEditModal('provincial')} sx={{ backgroundColor: '#336385', color: 'white', '&:hover': { backgroundColor: '#27496d' }, padding: '4px' }}>
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                        {festivosEditModal.provincial.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 150, overflowY: 'auto' }}>
                            {festivosEditModal.provincial.map((f) => (
                              <Box key={f.Id || f.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.5, backgroundColor: 'white', borderRadius: 1, fontSize: '0.85rem' }}>
                                <Box sx={{ overflow: 'hidden' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'medium', display: 'block' }}>{f.nombre}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(f.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                    {f.permanente && <RepeatIcon sx={{ fontSize: 10, ml: 0.5, verticalAlign: 'middle', color: '#4caf50' }} />}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex' }}>
                                  <IconButton size="small" onClick={() => handleEditarFestivo(f)} sx={{ padding: '2px' }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                                  <IconButton size="small" color="error" onClick={() => handleEliminarFestivoEditModal(f.Id || f.id)} sx={{ padding: '2px' }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1 }}>Sin festivos</Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: TIPO_CONFIG.municipal.bgColor, borderRadius: 1, border: `1px solid ${TIPO_CONFIG.municipal.color}30`, height: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationCityIcon sx={{ color: TIPO_CONFIG.municipal.color, fontSize: 18 }} /> Municipales
                          </Typography>
                          <IconButton size="small" onClick={() => openFestivoEditModal('municipal')} sx={{ backgroundColor: '#336385', color: 'white', '&:hover': { backgroundColor: '#27496d' }, padding: '4px' }}>
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                        {festivosEditModal.municipal.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 150, overflowY: 'auto' }}>
                            {festivosEditModal.municipal.map((f) => (
                              <Box key={f.Id || f.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.5, backgroundColor: 'white', borderRadius: 1, fontSize: '0.85rem' }}>
                                <Box sx={{ overflow: 'hidden' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'medium', display: 'block' }}>{f.nombre}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(f.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                    {f.permanente && <RepeatIcon sx={{ fontSize: 10, ml: 0.5, verticalAlign: 'middle', color: '#4caf50' }} />}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex' }}>
                                  <IconButton size="small" onClick={() => handleEditarFestivo(f)} sx={{ padding: '2px' }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                                  <IconButton size="small" color="error" onClick={() => handleEliminarFestivoEditModal(f.Id || f.id)} sx={{ padding: '2px' }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1 }}>Sin festivos</Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ p: 2, backgroundColor: COLORES_DIA.especial.bg, borderRadius: 1, border: `1px solid ${COLORES_DIA.especial.text}30` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon sx={{ color: COLORES_DIA.especial.text, fontSize: 18 }} /> Días Especiales (Jornada Reducida)
                          </Typography>
                          <IconButton size="small" onClick={openDiaEspecialEditModal} sx={{ backgroundColor: '#336385', color: 'white', '&:hover': { backgroundColor: '#27496d' }, padding: '4px' }}>
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Días laborables con horario reducido (ej: Nochebuena, Nochevieja)
                        </Typography>
                        {diasEspecialesEditModal.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {diasEspecialesEditModal.map((d) => (
                              <Box key={d.Id || d.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.5, px: 1, backgroundColor: 'white', borderRadius: 1 }}>
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 'medium' }}>{d.nombre}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                    ({new Date(d.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - {parseFloat(d.horas_trabajo)}h)
                                    {d.permanente && <RepeatIcon sx={{ fontSize: 10, ml: 0.5, verticalAlign: 'middle', color: '#4caf50' }} />}
                                  </Typography>
                                </Box>
                                <IconButton size="small" onClick={() => handleEditarDiaEspecial(d)} sx={{ padding: '2px' }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                                <IconButton size="small" color="error" onClick={() => handleEliminarDiaEspecialEditModal(d.Id || d.id)} sx={{ padding: '2px' }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1 }}>Sin días especiales</Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </>
            )}

            {!editandoCalendario && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOnIcon sx={{ fontSize: 16 }} />
                  <LocationCityIcon sx={{ fontSize: 16 }} />
                  <StarIcon sx={{ fontSize: 16 }} />
                  Los festivos provinciales, municipales y días especiales se pueden configurar despues de crear el calendario
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCalendarioModal(false)} sx={{ color: '#333' }}>Cancelar</Button>
            <Button type="submit" variant="contained" sx={{ backgroundColor: '#336385' }}>{editandoCalendario ? 'Guardar' : 'Crear Calendario'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={showVistaAnual} onClose={() => setShowVistaAnual(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CalendarMonthIcon />Vista Anual - Horas por Día</Box>
          <IconButton onClick={() => setShowVistaAnual(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {loadingVistaAnual ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><div className="spinner"></div></Box>
          ) : vistaAnualData ? (
            <>
              <Box sx={{ display: 'flex', gap: 3, mb: 3, mt: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 20, height: 20, borderRadius: 1, backgroundColor: COLORES_DIA.laboral.bg, border: `1px solid ${COLORES_DIA.laboral.text}` }} /><Typography variant="body2">Día laboral</Typography></Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 20, height: 20, borderRadius: 1, backgroundColor: COLORES_DIA.verano.bg, border: `1px solid ${COLORES_DIA.verano.text}` }} /><Typography variant="body2">Jornada verano</Typography></Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 20, height: 20, borderRadius: 1, backgroundColor: COLORES_DIA.festivo.bg, border: `1px solid ${COLORES_DIA.festivo.text}` }} /><Typography variant="body2">Festivo</Typography></Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 20, height: 20, borderRadius: 1, backgroundColor: COLORES_DIA.especial.bg, border: `1px solid ${COLORES_DIA.especial.text}` }} /><Typography variant="body2">Día especial</Typography></Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 20, height: 20, borderRadius: 1, backgroundColor: COLORES_DIA.finde.bg, border: `1px solid ${COLORES_DIA.finde.text}` }} /><Typography variant="body2">Fin de semana</Typography></Box>
              </Box>
              <Grid container spacing={2}>
                {vistaAnualData.meses.map((mes) => (<Grid item xs={6} sm={4} md={3} key={mes.mes}>{renderMesCalendario(mes)}</Grid>))}
              </Grid>

              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FlagIcon sx={{ color: '#ff9800' }} /> Festivos del Calendario
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, backgroundColor: TIPO_CONFIG.provincial.bgColor, borderRadius: 1, border: `1px solid ${TIPO_CONFIG.provincial.color}30` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon sx={{ color: TIPO_CONFIG.provincial.color }} /> Provinciales
                      </Typography>
                      <IconButton size="small" onClick={() => openFestivoCalendarioModal('provincial')} sx={{ backgroundColor: '#336385', color: 'white', '&:hover': { backgroundColor: '#27496d' } }}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {festivosCalendario.provincial.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {festivosCalendario.provincial.map((f) => (
                          <Box key={f.Id || f.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{f.nombre}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(f.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                                {f.permanente && <RepeatIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle', color: '#4caf50' }} />}
                              </Typography>
                            </Box>
                            <Box>
                              <IconButton size="small" onClick={() => { handleEditarFestivo(f); }}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleEliminarFestivoCalendario(f.Id || f.id)}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>Sin festivos provinciales</Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, backgroundColor: TIPO_CONFIG.municipal.bgColor, borderRadius: 1, border: `1px solid ${TIPO_CONFIG.municipal.color}30` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationCityIcon sx={{ color: TIPO_CONFIG.municipal.color }} /> Municipales
                      </Typography>
                      <IconButton size="small" onClick={() => openFestivoCalendarioModal('municipal')} sx={{ backgroundColor: '#336385', color: 'white', '&:hover': { backgroundColor: '#27496d' } }}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {festivosCalendario.municipal.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {festivosCalendario.municipal.map((f) => (
                          <Box key={f.Id || f.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{f.nombre}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(f.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                                {f.permanente && <RepeatIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle', color: '#4caf50' }} />}
                              </Typography>
                            </Box>
                            <Box>
                              <IconButton size="small" onClick={() => { handleEditarFestivo(f); }}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleEliminarFestivoCalendario(f.Id || f.id)}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>Sin festivos municipales</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ p: 2, backgroundColor: COLORES_DIA.especial.bg, borderRadius: 1, border: `1px solid ${COLORES_DIA.especial.text}30` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon sx={{ color: COLORES_DIA.especial.text }} /> Días Especiales (Jornada Reducida)
                  </Typography>
                  <IconButton size="small" onClick={openDiaEspecialModal} sx={{ backgroundColor: '#336385', color: 'white', '&:hover': { backgroundColor: '#27496d' } }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Días laborables con horario reducido (ej: Nochebuena, Nochevieja)
                </Typography>
                {diasEspecialesCalendario.length > 0 ? (
                  <Grid container spacing={1}>
                    {diasEspecialesCalendario.map((d) => (
                      <Grid item xs={12} sm={6} md={4} key={d.Id || d.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, backgroundColor: 'white', borderRadius: 1 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{d.nombre}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(d.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })} - <strong>{parseFloat(d.horas_trabajo)}h</strong>
                              {d.permanente && <RepeatIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle', color: '#4caf50' }} />}
                            </Typography>
                          </Box>
                          <Box>
                            <IconButton size="small" onClick={() => handleEditarDiaEspecial(d)}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => handleEliminarDiaEspecial(d.Id || d.id)}><DeleteIcon fontSize="small" /></IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>Sin días especiales configurados</Typography>
                )}
              </Box>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={showDiaEspecialModal} onClose={() => setShowDiaEspecialModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>
          {editandoDiaEspecial ? 'Editar Día Especial' : 'Nuevo Día Especial'}
        </DialogTitle>
        <form onSubmit={showVistaAnual ? handleGuardarDiaEspecial : handleGuardarDiaEspecialEditModal}>
          <DialogContent>
            <TextField
              fullWidth margin="normal" label="Fecha" type="date"
              value={diaEspecialForm.fecha}
              onChange={(e) => {
                const fecha = e.target.value;
                const anio = fecha ? new Date(fecha).getFullYear() : diaEspecialForm.anio;
                setDiaEspecialForm({ ...diaEspecialForm, fecha, anio });
              }}
              InputLabelProps={{ shrink: true }} required
            />
            <TextField
              fullWidth margin="normal" label="Nombre"
              value={diaEspecialForm.nombre}
              onChange={(e) => setDiaEspecialForm({ ...diaEspecialForm, nombre: e.target.value })}
              placeholder="Ej: Nochebuena, Nochevieja"
              required
            />
            <TextField
              fullWidth margin="normal" label="Horas de trabajo"
              type="number" value={diaEspecialForm.horas_trabajo}
              onChange={(e) => setDiaEspecialForm({ ...diaEspecialForm, horas_trabajo: parseFloat(e.target.value) || 0 })}
              inputProps={{ min: 0, max: 12, step: 0.25 }}
              helperText="Horas que se trabajan en este día especial"
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={diaEspecialForm.permanente}
                  onChange={(e) => setDiaEspecialForm({ ...diaEspecialForm, permanente: e.target.checked })}
                />
              }
              label="Permanente (se repite todos los años)"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDiaEspecialModal(false)} sx={{ color: '#333' }}>Cancelar</Button>
            <Button type="submit" variant="contained" sx={{ backgroundColor: '#336385' }}>
              {editandoDiaEspecial ? 'Guardar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={showDuplicarModal} onClose={() => setShowDuplicarModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>Duplicar Calendario</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>Duplicar "{calendarioDuplicar?.nombre}" para el año:</Typography>
          <TextField fullWidth label="Nuevo Año" type="number" value={nuevoAnioDuplicar} onChange={(e) => setNuevoAnioDuplicar(parseInt(e.target.value))} inputProps={{ min: 2020, max: 2050 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDuplicarModal(false)} sx={{ color: '#333' }}>Cancelar</Button>
          <Button onClick={handleDuplicarCalendario} variant="contained" sx={{ backgroundColor: '#336385' }}>Duplicar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showFestivoModal} onClose={() => setShowFestivoModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>{editandoFestivo ? 'Editar Festivo' : `Nuevo Festivo ${TIPO_CONFIG[festivoForm.tipo]?.label}`}</DialogTitle>
        <form onSubmit={(festivoForm.tipo === 'provincial' || festivoForm.tipo === 'municipal')
          ? (showVistaAnual ? handleGuardarFestivoCalendario : handleGuardarFestivoEditModal)
          : handleGuardarFestivo}>
          <DialogContent>
            <TextField fullWidth margin="normal" label="Fecha" type="date" value={festivoForm.fecha} onChange={(e) => { const fecha = e.target.value; const anio = fecha ? new Date(fecha).getFullYear() : festivoForm.anio; setFestivoForm({ ...festivoForm, fecha, anio }); }} InputLabelProps={{ shrink: true }} required />
            <TextField fullWidth margin="normal" label="Nombre del Festivo" value={festivoForm.nombre} onChange={(e) => setFestivoForm({ ...festivoForm, nombre: e.target.value })} placeholder="Ej: Día de la Constitucion" required />
            {festivoForm.tipo === 'provincial' && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Provincia</InputLabel>
                <Select value={festivoForm.provincia} label="Provincia" onChange={(e) => setFestivoForm({ ...festivoForm, provincia: e.target.value })} required>
                  {PROVINCIAS.map((prov) => (<MenuItem key={prov} value={prov}>{prov}</MenuItem>))}
                </Select>
              </FormControl>
            )}
            {festivoForm.tipo === 'municipal' && (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Provincia</InputLabel>
                  <Select value={festivoForm.provincia} label="Provincia" onChange={(e) => setFestivoForm({ ...festivoForm, provincia: e.target.value })} required>
                    {PROVINCIAS.map((prov) => (<MenuItem key={prov} value={prov}>{prov}</MenuItem>))}
                  </Select>
                </FormControl>
                <TextField fullWidth margin="normal" label="Municipio" value={festivoForm.municipio} onChange={(e) => setFestivoForm({ ...festivoForm, municipio: e.target.value })} placeholder="Ej: Valencia, Paterna, Torrent..." required />
              </>
            )}
            <FormControlLabel control={<Checkbox checked={festivoForm.permanente} onChange={(e) => setFestivoForm({ ...festivoForm, permanente: e.target.checked })} />} label="Permanente (se repite todos los años)" sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowFestivoModal(false)} sx={{ color: '#333' }}>Cancelar</Button>
            <Button type="submit" variant="contained" sx={{ backgroundColor: '#336385' }}>{editandoFestivo ? 'Guardar' : 'Crear'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={showPeriodoModal} onClose={() => setShowPeriodoModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>{editandoPeriodo ? 'Editar Periodo de Verano' : 'Nuevo Periodo de Verano'}</DialogTitle>
        <form onSubmit={handleGuardarPeriodo}>
          <DialogContent>
            <TextField fullWidth margin="normal" label="Nombre" value={periodoForm.nombre} onChange={(e) => setPeriodoForm({ ...periodoForm, nombre: e.target.value })} placeholder="Ej: Periodo Verano Julio-Agosto" helperText="Opcional - Si se deja vacío, se generará automáticamente" />
            <TextField fullWidth margin="normal" label="Año" type="number" value={periodoForm.anio} onChange={(e) => setPeriodoForm({ ...periodoForm, anio: parseInt(e.target.value) })} required />
            <Grid container spacing={2}>
              <Grid item xs={6}><TextField fullWidth margin="normal" label="Fecha Inicio" type="date" value={periodoForm.fecha_inicio} onChange={(e) => setPeriodoForm({ ...periodoForm, fecha_inicio: e.target.value })} InputLabelProps={{ shrink: true }} required /></Grid>
              <Grid item xs={6}><TextField fullWidth margin="normal" label="Fecha Fin" type="date" value={periodoForm.fecha_fin} onChange={(e) => setPeriodoForm({ ...periodoForm, fecha_fin: e.target.value })} InputLabelProps={{ shrink: true }} required /></Grid>
            </Grid>
            {periodoForm.fecha_inicio && periodoForm.fecha_fin && (
              <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1, textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">Duracion: <strong>{Math.ceil((new Date(periodoForm.fecha_fin) - new Date(periodoForm.fecha_inicio)) / (1000 * 60 * 60 * 24)) + 1} días</strong></Typography>
              </Box>
            )}
            <TextField fullWidth margin="normal" label="Días Obligatorios Mínimos" type="number" value={periodoForm.dias_obligatorios} onChange={(e) => setPeriodoForm({ ...periodoForm, dias_obligatorios: parseInt(e.target.value) || 10 })} inputProps={{ min: 1, max: 30 }} helperText="Número mínimo de días que cada empleado debe tomar durante este periodo" required />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPeriodoModal(false)} sx={{ color: '#333' }}>Cancelar</Button>
            <Button type="submit" variant="contained" sx={{ backgroundColor: '#336385' }}>{editandoPeriodo ? 'Guardar' : 'Crear'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <ToastContainer />
    </div>
  );
}

export default GestorCalendariosLaborales;
